import { zodResolver } from '@hookform/resolvers/zod';
import { ReturnToProductLibraryButton } from '@pages/ManageProductPage/components/ReturnToProductLibraryButton';
import { isJobInProgress } from '@partful/pages/product-library/ui/product-list/jobFiltering';
import { useInvalidateQueries } from '@partful/shared/lib/hooks/useInvalidateQueries';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { PartCategoriesJob } from '@providers/job/types';
import { useTenant } from '@providers/Tenant';
import { useUser } from '@providers/User';
import { GA } from '@services/gtag';
import { productInventoryEndpoints } from '@services/productInventory';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDecodedParams } from 'src/hooks/useDecodedParams';
import { usePartsCategoriesTimeoutHandler } from './lib/hooks/usePartsCategoriesTimeoutHandler';
import { PartsCategoriesFormSchema, partsCategoriesFormSchema } from './lib/PartsCategoriesFormSchema';
import { PartsCategoriesFailed } from './ui/PartsCategoriesFailed';
import { PartsCategoriesPreview } from './ui/PartsCategoriesPreview';
import { PartsCategoriesProcessing } from './ui/PartsCategoriesProcessing';
import { PartsCategoriesTabs } from './ui/PartsCategoriesTabs';

const REFETCH_JOB_INTERVAL = 10000;

export function PartsCategories() {
  const { role, username } = useUser();
  const { accountId } = useTenant();
  const { productId } = useDecodedParams<{ productId: string }>();
  const { configurationPID } = useQueryParams();

  const { invalidateContentProcessingJobsQueries } = useInvalidateQueries();

  const form = useForm<PartsCategoriesFormSchema>({
    resolver: zodResolver(partsCategoriesFormSchema),
    mode: 'onChange',
  });

  const { data: jobs, isLoading: isDeterminingInitialJobStatus } = useProductInventoryQuery<PartCategoriesJob[]>(
    productInventoryEndpoints.contentProcessing.root,
    {
      account: accountId,
      jobType: 'PartCategories',
      configurationPID: configurationPID!,
    }
  );

  // this is the source of truth for the job status, so if the page is refreshed, it will show the correct job status
  const partCategoriesJobData = jobs?.[0];
  const isPreviewStatus = partCategoriesJobData?.status === 'IN_PROGRESS_PREVIEW';

  // if there is an existing in progress job, then start polling this job - this is a local variable derived from the source of truth
  const inProgressPartCategoriesJobRunID =
    partCategoriesJobData && isJobInProgress(partCategoriesJobData) && !isPreviewStatus
      ? partCategoriesJobData?.runID
      : null;

  const { data: inProgressPartCategoriesJobData } = useProductInventoryQuery<PartCategoriesJob>(
    `${productInventoryEndpoints.contentProcessing.partsCategories}/${inProgressPartCategoriesJobRunID}`,
    { account: accountId },
    {
      enabled: Boolean(inProgressPartCategoriesJobRunID),
      refetchInterval: REFETCH_JOB_INTERVAL,
      notifyOnChangeProps: 'all',
    }
  );

  usePartsCategoriesTimeoutHandler(inProgressPartCategoriesJobData, accountId);

  useEffect(() => {
    /* istanbul ignore next - this will be better tested as an e2e test */
    if (partCategoriesJobData?.status === 'COMPLETED') {
      form.reset();
    }
  }, [partCategoriesJobData, form]);

  useEffect(() => {
    /* istanbul ignore next - this will be better tested as an e2e test */
    if (['FAILED', 'IN_PROGRESS_PREVIEW'].includes(inProgressPartCategoriesJobData?.status ?? '')) {
      // invalidate the queries first so the page doesn't show the loading screen and shows the correct job status
      invalidateContentProcessingJobsQueries();
    }
  }, [inProgressPartCategoriesJobData, invalidateContentProcessingJobsQueries]);

  useEffect(() => {
    const eventData = {
      user: username,
      account: accountId,
      product: productId,
      role,
    };

    GA.gtag('event', 'manage_product_parts_categories_visits', eventData);
  }, [accountId, productId, username, role]);

  if (isDeterminingInitialJobStatus) {
    return null;
  }

  const isInProgress =
    !isPreviewStatus &&
    ((partCategoriesJobData && isJobInProgress(partCategoriesJobData)) ||
      /* istanbul ignore next */ (inProgressPartCategoriesJobData && isJobInProgress(inProgressPartCategoriesJobData)));

  const isFailed = partCategoriesJobData?.status === 'FAILED';

  const canShowPartsCategories = !isInProgress && !isFailed && !isPreviewStatus;

  return (
    <>
      <FormProvider {...form}>
        {isInProgress && <PartsCategoriesProcessing accountId={accountId} runID={partCategoriesJobData?.runID!} />}
        {isFailed && (
          <PartsCategoriesFailed
            accountId={accountId}
            runID={partCategoriesJobData.runID}
            errors={partCategoriesJobData.errors}
          />
        )}
        {isPreviewStatus && <PartsCategoriesPreview runID={partCategoriesJobData?.runID!} />}
        {canShowPartsCategories && <PartsCategoriesTabs />}
      </FormProvider>
      <ReturnToProductLibraryButton />
    </>
  );
}
